import {isReadableError} from '@/lib/api/ReadableError'
import throwIfError from '@/lib/api/throwIfError'
import cartDb from '@/lib/cart/cartDb'
import {getLanguageCountryFromLocale, type SupportedLocales} from '@/lib/locale/locale'
import call from '@/utils/api/call'
import shouldLogError from '@/utils/api/shouldLogError'
import UnexpectedError from '@/utils/api/UnexpectedError'
import UserFriendlyError from '@/utils/errors/UserFriendlyError'

import {updateCartWith} from './cartService'

const NON_ERROR_API_CODES = ['IAA0000']

export async function signIn({
  email,
  password,
  signal,
  locale,
}: {
  email: string
  password: string
  signal?: AbortSignal
  locale: SupportedLocales
}) {
  return call
    .post(
      `/api/auth/v1/accounts/login`,
      {
        email,
        password,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(async res => {
      throwIfError(res, NON_ERROR_API_CODES)

      const {country} = getLanguageCountryFromLocale(locale)

      // Sync guest cart to user cart
      await updateCartWith({
        isAuthenticated: true,
        items: cartDb.getCart(country).items,
        locale,
      }).then(() => {
        cartDb.clearCart(country)
      })
    })
    .catch(e => {
      if (isReadableError(e) && e.res.data.msg && typeof e.res.data.msg === 'string') {
        throw new UserFriendlyError(e.res.data.msg, e).permanent()
      }

      if (shouldLogError(e)) {
        console.error('Cannot login', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function signInWithGoogle({
  token,
  referrerId,
  signal,
}: {
  token: string
  referrerId?: string
  signal?: AbortSignal
}) {
  return call
    .post(
      `/api/auth/v1/accounts/google-login`,
      {
        token,
        referrerId,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
    })
    .catch(e => {
      if (isReadableError(e) && e.res.data.msg && typeof e.res.data.msg === 'string') {
        throw new UserFriendlyError(e.res.data.msg, e).permanent()
      }

      if (shouldLogError(e)) {
        console.error('Cannot login', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function signUp({
  email,
  fullName,
  phone,
  description,
  password,
  signal,
  referrerId,
}: {
  password: string
  email: string
  fullName: string
  phone?: string
  description?: string
  referrerId?: string
  signal?: AbortSignal
}) {
  return call
    .post(
      `/api/auth/v1/accounts`,
      {
        email,
        accountName: fullName,
        password,
        phone,
        description,
        referrerId: referrerId ? referrerId.split('/').pop() : undefined,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      throwIfError(res, NON_ERROR_API_CODES)
    })
    .catch(e => {
      if (isReadableError(e) && e.res.data.msg && typeof e.res.data.msg === 'string') {
        throw new UserFriendlyError(e.res.data.msg, e).permanent()
      }

      if (shouldLogError(e)) {
        console.error('Cannot create account', e)
      }
      throw new UnexpectedError(e)
    })
}

export async function triggerResetPassword({email, signal}: {email: string; signal?: AbortSignal}) {
  return call
    .post(
      `/api/auth/v1/accounts/trigger-password-reset`,
      {
        email,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot trigger reset password', e)
      }
      return false
    })
}

export async function resetPassword({
  token,
  password,
  signal,
}: {
  token: string
  password: string
  signal?: AbortSignal
}) {
  return call
    .post(
      `/api/auth/v1/accounts/reset-password`,
      {
        token,
        newPassword: password,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot reset password', e)
      }
      return false
    })
}

export async function logout({signal}: {signal?: AbortSignal}) {
  return call
    .post(
      `/api/auth/v1/accounts/logout`,
      {},
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot logout', e)
      }
      return false
    })
}

export async function changePassword({
  currentPassword,
  password,
  signal,
}: {
  currentPassword: string
  password: string
  signal?: AbortSignal
}) {
  return call
    .post(
      `/api/auth/v1/accounts/change-password`,
      {
        oldPassword: currentPassword,
        newPassword: password,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot change password', e)
      }
      return false
    })
}

export async function triggerAccountDeletion({signal}: {signal?: AbortSignal}) {
  return call
    .post(
      `/api/auth/v1/accounts/trigger-delete`,
      {},
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot request account deletion', e)
      }
      return false
    })
}

export async function deleteAccount({signal, code}: {signal?: AbortSignal; code: string}) {
  return call
    .post(
      `/api/auth/v1/accounts/delete`,
      {
        code,
      },
      {
        signal,
        withCredentials: true,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot delete account', e)
      }
      return false
    })
}

export async function updateName({
  signal,
  firstName,
  lastName,
}: {
  signal?: AbortSignal
  firstName: string
  lastName: string
}) {
  return call
    .patch(
      `/api/auth/v1/accounts`,
      {
        accountName: `${firstName} ${lastName}`,
      },
      {
        signal,
      },
    )
    .then(res => {
      const msg = throwIfError(res, NON_ERROR_API_CODES, true)
      if (msg) return msg
      return true
    })
    .catch(e => {
      if (shouldLogError(e)) {
        console.error('Cannot update name', e)
      }
      return false
    })
}
